/**
 * Page following — «اتّباع القارئ», §5.7.
 *
 * The ONLY thing that moves the page during playback. It moves the ROUTE, never the
 * audio element (A16/L11).
 */
import { useEffect, useRef } from 'react'
import { pageOfVerse, type Meta } from '@mushaf/corpus'
import { usePlayer } from '../stores/player'
import { useSettings } from '../stores/settings'

export function usePageFollow(meta: Meta | undefined, navigate: (page: number) => void) {
  const follow = useSettings((s) => s.followRecitation)
  const playingKey = usePlayer((s) => s.currentKey)

  /** The last page follow moved the view to. */
  const followedPage = useRef<number | null>(null)

  useEffect(() => {
    if (!follow) {
      // Re-arming on release means switching follow back on catches up with the
      // reciter IMMEDIATELY instead of waiting for the next page break (T23).
      followedPage.current = null
      return
    }
    if (!playingKey || !meta) return

    const target = pageOfVerse(meta.pages, playingKey)

    /**
     * A15 — KEY ON THE RECITER'S PAGE, NEVER ON "view ≠ reciter".
     *
     * Follow must react to the RECITER crossing a page break. Comparing against the
     * currently displayed page re-fires the moment the reader turns a page by hand —
     * playback is still mid-page, so the effect drags the view straight back.
     * Symptom: "the arrows are dead."
     *
     * Keyed on the reciter's page, a manual turn stands until the recitation
     * genuinely moves on (T22).
     */
    if (target === null || target === followedPage.current) return
    followedPage.current = target
    navigate(target)
    // `navigate` is a stable router call; including it would re-run this on every
    // render of the host component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [follow, playingKey, meta])
}
